import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Package, Check, Loader2, Camera, Lock, AlertCircle, Warehouse } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { api, getBackend } from '@/api/apiClient';
import { useRepProfile } from '@/lib/RepProfileContext';
import { useCompanyInfo } from '@/lib/useCompanyInfo';
import { cur } from '@/lib/currency';

/**
 * Add / edit a product — SellGo's own database only (on Odoo, products are
 * managed in Odoo). Routes: /products/new, /products/:id/edit.
 * Managers, and reps allowed to add products; only a manager's new product
 * can receive an opening quantity (into the main store).
 */

// The units shops count in; any other name can be typed.
const UNITS = ['حبة', 'كرتون', 'شدة', 'علبة', 'باكت', 'كيس', 'درزن', 'كيلو', 'لتر'];

const EMPTY = {
  name: '', default_code: '', barcode: '', category: '', uom: 'حبة',
  list_price: '', standard_price: '', tax_rate: '', description: '', image: '', qty: '',
};

// 0.15 → "15": the VAT field is in percent.
const toPercent = (rate) => (rate === null || rate === undefined || rate === '' ? '' : String(Math.round(Number(rate) * 10000) / 100));

const money = (v) => Number(v || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Shrink a photo to at most 640 px as JPEG, small enough to keep with the product.
function shrinkPhoto(file, max = 640) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';   // JPEG has no transparency
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('bad image')); };
    img.src = url;
  });
}

function Section({ title, children }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
      <p className="text-sm font-bold text-foreground">{title}</p>
      {children}
    </div>
  );
}

function Chips({ options, value, onPick }) {
  return (
    <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1">
      {options.map(o => (
        <button key={o} type="button" onClick={() => onPick(o)}
          className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${value === o ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

export default function ProductForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { profile, isManager, loading: profileLoading } = useRepProfile();
  const company = useCompanyInfo();
  const allowed = getBackend() === 'supabase' && (isManager || !!profile.allow_add_product);

  const [form, setForm] = useState(EMPTY);
  const [initial, setInitial] = useState({ tax_rate: '', image: '' });   // edit: what was loaded
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const fileRef = useRef(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  useEffect(() => {
    if (!allowed) return;
    api.get('/products/categories')
      .then(res => { if (res?.success) setCategories((res.data || []).map(c => c.name)); })
      .catch(() => {});
  }, [allowed]);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get(`/products/${parseInt(id)}`);
      const p = res?.data;
      if (!res?.success || !p) throw new Error(res?.error || 'فشل تحميل المنتج');
      const tax = toPercent(p.tax_rate);
      setForm({
        name: p.name || '',
        default_code: p.default_code || '',
        barcode: p.barcode || '',
        category: p.category_name || '',
        uom: p.uom_name || '',
        list_price: p.list_price != null ? String(p.list_price) : '',
        standard_price: Number(p.standard_price) ? String(p.standard_price) : '',
        tax_rate: tax,
        description: p.description || '',
        image: p.image_src || '',
        qty: '',
      });
      setInitial({ tax_rate: tax, image: p.image_src || '' });
    } catch (e) {
      setLoadError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isEdit && allowed) load();
  }, [id, allowed]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';   // picking the same photo again still fires
    if (!file) return;
    setPhotoBusy(true);
    try {
      set('image', await shrinkPhoto(file));
    } catch {
      toast.error('تعذّر قراءة الصورة، اختر صورة أخرى');
    }
    setPhotoBusy(false);
  };

  const companyVat = Number(company.vatRate) || 0;
  const vatRate = form.tax_rate.trim() === '' ? companyVat : (Number(form.tax_rate) || 0) / 100;
  const price = parseFloat(form.list_price);

  const submit = async () => {
    const name = form.name.trim();
    const tax = form.tax_rate.trim();
    if (!name) { toast.error('اسم المنتج مطلوب'); return; }
    if (!(price >= 0)) { toast.error('سعر البيع مطلوب'); return; }
    if (tax !== '' && !(Number(tax) >= 0 && Number(tax) <= 100)) { toast.error('نسبة الضريبة غير صحيحة'); return; }

    const body = {
      name,
      default_code: form.default_code.trim(),
      barcode: form.barcode.trim(),
      category: form.category.trim(),
      uom: form.uom.trim(),
      list_price: price,
      description: form.description.trim(),
    };
    if (form.standard_price.trim() !== '') body.standard_price = parseFloat(form.standard_price) || 0;
    if (isEdit) {
      body.id = Number(id);
      // Untouched VAT stays as it is (it may be the company's rate, not the product's own).
      if (tax !== initial.tax_rate) body.tax_rate = tax === '' ? '' : Number(tax) / 100;
      if (form.image !== initial.image) body.image = form.image;
    } else {
      if (tax !== '') body.tax_rate = Number(tax) / 100;
      if (form.image) body.image = form.image;
      const qty = parseFloat(form.qty);
      if (isManager && qty > 0) body.qty = qty;
    }

    setSaving(true);
    try {
      const res = await api.post('/sellgo_product_save', body);
      if (isEdit) {
        toast.success('تم حفظ التعديلات');
        navigate(`/products/${id}`, { replace: true });
      } else {
        toast.success('تم إضافة المنتج');
        setCreated(res.data);
      }
    } catch (e) {
      toast.error(e?.status === 0 ? 'لا يوجد اتصال بالإنترنت، حاول مرة أخرى' : (e?.message || 'فشل حفظ المنتج'));
    }
    setSaving(false);
  };

  if (profileLoading || (isEdit && loading && allowed)) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (!allowed) return (
    <div className="p-6 flex flex-col items-center gap-4 text-center" dir="rtl">
      <Lock className="w-12 h-12 text-muted-foreground" />
      <p className="font-semibold text-foreground">ليس لديك صلاحية إضافة أو تعديل المنتجات</p>
      <Button variant="outline" onClick={() => navigate('/products')}>العودة للمنتجات</Button>
    </div>
  );

  if (loadError) return (
    <div className="p-6 flex flex-col items-center gap-4" dir="rtl">
      <AlertCircle className="w-12 h-12 text-red-500" />
      <p className="font-semibold text-foreground text-center">{loadError}</p>
      <Button onClick={load} variant="outline">إعادة المحاولة</Button>
    </div>
  );

  // Success screen (new product)
  if (created) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-6 gap-6" dir="rtl">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
          <Check className="w-10 h-10 text-emerald-600" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold text-foreground">تم إضافة المنتج!</h2>
          <p className="text-muted-foreground mt-1">{created.name}</p>
          {Number(created.qty_available) > 0 && (
            <p className="text-sm text-emerald-600 font-semibold mt-1">
              رصيد افتتاحي {Number(created.qty_available).toLocaleString('ar-EG')} {created.uom_name} في المخزن الرئيسي
            </p>
          )}
        </div>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <Button className="w-full bg-primary" onClick={() => navigate(`/products/${created.id}`)}>
            عرض المنتج
          </Button>
          <Button variant="outline" className="w-full"
            onClick={() => { setCreated(null); setForm({ ...EMPTY, category: form.category, uom: form.uom, tax_rate: form.tax_rate }); }}>
            إضافة منتج آخر
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => navigate('/products')}>
            العودة للقائمة
          </Button>
        </div>
      </div>
    );
  }

  const unitOptions = form.uom && !UNITS.includes(form.uom) ? [form.uom, ...UNITS] : UNITS;
  const categoryOptions = form.category && !categories.includes(form.category) ? [form.category, ...categories] : categories;

  return (
    <div className="p-4 pb-32 space-y-4" dir="rtl">
      <div className="flex items-center gap-2">
        <Package className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-bold text-foreground">{isEdit ? 'تعديل المنتج' : 'منتج جديد'}</h2>
      </div>

      {/* Photo */}
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={photoBusy}
          className="w-24 h-24 rounded-2xl border-2 border-dashed border-border bg-muted/40 flex items-center justify-center overflow-hidden flex-shrink-0">
          {photoBusy ? <Loader2 className="w-6 h-6 animate-spin text-primary" />
            : form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" />
            : <Camera className="w-7 h-7 text-muted-foreground" />}
        </button>
        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-foreground">صورة المنتج</p>
          <p className="text-xs text-muted-foreground">اختيارية — تظهر في قائمة المنتجات وشاشة البيع</p>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={photoBusy}>
              {form.image ? 'تغيير الصورة' : 'اختيار صورة'}
            </Button>
            {form.image && (
              <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => set('image', '')}>
                حذف
              </Button>
            )}
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} />
      </div>

      <Section title="البيانات الأساسية">
        <div>
          <label className="text-xs font-semibold text-muted-foreground">اسم المنتج *</label>
          <Input value={form.name} onChange={e => set('name', e.target.value)}
            placeholder="مثال: حليب طازج كامل الدسم 1 لتر" className="mt-1 rounded-xl" autoFocus={!isEdit} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">الكود</label>
            <Input value={form.default_code} onChange={e => set('default_code', e.target.value)}
              placeholder="MLK-1L" className="mt-1 rounded-xl" dir="ltr" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">الباركود</label>
            <Input value={form.barcode} onChange={e => set('barcode', e.target.value)}
              placeholder="628xxxxxxxxxx" inputMode="numeric" className="mt-1 rounded-xl" dir="ltr" />
          </div>
        </div>
      </Section>

      <Section title="الفئة والوحدة">
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground">الفئة</label>
          {categoryOptions.length > 0 && (
            <Chips options={categoryOptions} value={form.category} onPick={v => set('category', form.category === v ? '' : v)} />
          )}
          <Input value={form.category} onChange={e => set('category', e.target.value)}
            placeholder="اختر من الأعلى أو اكتب فئة جديدة" className="rounded-xl" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-muted-foreground">وحدة البيع</label>
          <Chips options={unitOptions} value={form.uom} onPick={v => set('uom', v)} />
          <Input value={form.uom} onChange={e => set('uom', e.target.value)}
            placeholder="أو اكتب وحدة أخرى" className="rounded-xl" />
        </div>
      </Section>

      <Section title="السعر والضريبة">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">سعر البيع ({cur()}) *</label>
            <Input value={form.list_price} onChange={e => set('list_price', e.target.value)}
              placeholder="0.00" type="number" min="0" step="any" inputMode="decimal" className="mt-1 rounded-xl" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">التكلفة ({cur()})</label>
            <Input value={form.standard_price} onChange={e => set('standard_price', e.target.value)}
              placeholder="0.00" type="number" min="0" step="any" inputMode="decimal" className="mt-1 rounded-xl" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">ضريبة القيمة المضافة (%)</label>
          <Input value={form.tax_rate} onChange={e => set('tax_rate', e.target.value)}
            placeholder={`${toPercent(companyVat)} (نسبة الشركة)`} type="number" min="0" max="100" step="any" inputMode="decimal"
            className="mt-1 rounded-xl" />
          <p className="text-xs text-muted-foreground mt-1">اتركها فاضية لتطبيق نسبة الشركة، أو اكتب 0 لمنتج معفى.</p>
        </div>
        {price > 0 && (
          <div className="flex justify-between items-center bg-primary/5 rounded-xl px-3 py-2">
            <span className="text-xs text-muted-foreground">السعر للعميل شامل الضريبة</span>
            <span className="text-sm font-bold text-primary">{money(price * (1 + vatRate))} {cur()}</span>
          </div>
        )}
      </Section>

      {!isEdit && isManager && (
        <Section title="الرصيد الافتتاحي">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">الكمية الموجودة الآن{form.uom ? ` (${form.uom})` : ''}</label>
            <Input value={form.qty} onChange={e => set('qty', e.target.value)}
              placeholder="0" type="number" min="0" step="any" inputMode="decimal" className="mt-1 rounded-xl" />
          </div>
          <p className="text-xs text-muted-foreground flex items-start gap-1.5">
            <Warehouse className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            تُضاف للمخزن الرئيسي، ومنه تحوّلها لعربيات المندوبين.
          </p>
        </Section>
      )}

      <Section title="الوصف">
        <Textarea value={form.description} onChange={e => set('description', e.target.value)}
          placeholder="الحجم، العبوة، بلد المنشأ... (اختياري)" className="rounded-xl" rows={3} />
      </Section>

      {/* Actions */}
      <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border p-4 flex gap-2 z-30">
        <Button variant="outline" onClick={() => navigate(isEdit ? `/products/${id}` : '/products')} className="flex-1" disabled={saving}>
          إلغاء
        </Button>
        <Button onClick={submit} disabled={saving || photoBusy} className="flex-[2] bg-primary gap-2">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> جاري الحفظ...</>
            : <><Check className="w-4 h-4" /> {isEdit ? 'حفظ التعديلات' : 'إضافة المنتج'}</>}
        </Button>
      </div>
    </div>
  );
}
